import {
  PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceQuestionnaireElement,
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceTemplateQuestionnaireByReviewerStrategyFragment,
  QuestionnaireContentFragment,
} from 'generated/resources/graphql'
import { mapValues } from 'lodash'

type QuestionnairableContentFragments =
  | PerformanceQuestionnaireByReviewerStrategyFragment
  | PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment
  | PerformanceTemplateQuestionnaireByReviewerStrategyFragment

export type PerformanceRawQuestionnaires = Pick<
  QuestionnairableContentFragments,
  | 'defaultRatingScale'
  | 'selfQuestionnaire'
  | 'managerQuestionnaire'
  | 'directReportQuestionnaire'
  | 'peersQuestionnaire'
  | 'employeeScoreSelfQuestionnaire'
  | 'employeeScoreManagerQuestionnaire'
  | 'employeePotentialScoreManagerQuestionnaire'
  | 'competenciesPlaceholderSelfQuestionnaire'
  | 'competenciesPlaceholderManagerQuestionnaire'
>

// TODO: remove after all consumers use hook instead of lib
// Performance review process and custom templates returns an entity with an id (the questionnairable id)
// predefined templates returns a value object without id,
export type PerformanceRawQuestionnairesEntity = PerformanceRawQuestionnaires & {
  id: number
}

export interface StrategyQuestionnaire<T> {
  general: T[]
  competencies: T[]
  employeeScore: T[]
  employeePotentialScore: T[]
}

export type StrategiesQuestionnaires<T> = Record<
  PerformanceReviewerStrategiesStrategyEnum,
  StrategyQuestionnaire<T>
>

function countTotalQuestions(
  questionnaireContent: StrategyQuestionnaire<QuestionnaireContentFragment>
): number {
  const allContent = [
    ...questionnaireContent.general,
    ...questionnaireContent.employeeScore,
    ...questionnaireContent.employeePotentialScore,
  ]

  return allContent.reduce((total, q) => total + q.questions.length, 0)
}

/**
 * Hook for transforming raw performance questionnaires into a strategy-organized structure.
 *
 * The backend returns questionnaires (general, final questions, employee potential scores,
 * and competencies) where each questionnaire contains embedded strategy configurations.
 * The frontend requires the inverse: questionnaires organized by strategy for easier access
 * and consistent API usage.
 *
 * This hook performs the data transformation, providing a unified interface where
 * questionnaires are keyed by strategy rather than containing strategy data internally.
 *
 */
export const useQuestionnairesRawContent = (rawQuestionnaires: PerformanceRawQuestionnaires) => {
  const selfRawContent = {
    general: rawQuestionnaires?.selfQuestionnaire?.content ?? [],
    employeeScore: rawQuestionnaires?.employeeScoreSelfQuestionnaire?.content ?? [],
    competencies: rawQuestionnaires?.competenciesPlaceholderSelfQuestionnaire?.content ?? [],
    employeePotentialScore: [],
  }

  const managerRawContent = {
    general: rawQuestionnaires?.managerQuestionnaire?.content ?? [],
    employeeScore: rawQuestionnaires?.employeeScoreManagerQuestionnaire?.content ?? [],
    competencies: rawQuestionnaires?.competenciesPlaceholderManagerQuestionnaire?.content ?? [],
    employeePotentialScore:
      rawQuestionnaires?.employeePotentialScoreManagerQuestionnaire?.content ?? [],
  }

  const directReportsRawContent = {
    general: rawQuestionnaires?.directReportQuestionnaire?.content ?? [],
    employeeScore: [],
    competencies: [],
    employeePotentialScore: [],
  }

  const peersRawContent = {
    general: rawQuestionnaires?.peersQuestionnaire?.content ?? [],
    employeeScore: [],
    competencies: [],
    employeePotentialScore: [],
  }

  const rawQuestionnairesContentByStrategies: StrategiesQuestionnaires<PerformanceQuestionnaireElement> =
    {
      self: selfRawContent,
      manager: managerRawContent,
      direct_reports: directReportsRawContent,
      peers: peersRawContent,
    }

  const totalQuestionsByStrategies: Record<PerformanceReviewerStrategiesStrategyEnum, number> =
    mapValues(rawQuestionnairesContentByStrategies, (content) => countTotalQuestions(content))

  return { rawQuestionnairesContentByStrategies, totalQuestionsByStrategies }
}
